import { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	useFirePostOfficeTrigger,
	useTrigger,
} from '@atlassian/post-office-context/post-office-trigger';

import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';
import { CANONICAL_TEMPLATE_KEYS } from '@post-office/growth-common/confluence-experiments-context/template-xflow';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { getHomeTwcXflowModalLink } from '@post-office/message-templates-common--growth-starburst/utils/get-home-twc-xflow-modal-link';
import { type MessageTemplateContext } from '@post-office/message-template--twc-sidenav-banner';
import { type WaitForTriggerFunction } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { TEMPLATE_XFLOW_EXPERIMENT_KEY } from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { TapProvider } from '@post-office/util-tap-traits';

import { PersonalizedBanner } from '../../../components/personalized-banner';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { getAtlassianHomeUrl, getLoomOnboardingUrl } from '../../../utils/get-onboarding-url';
import { getTemplateXflowReminderTapTrait } from './getTemplateXflowReminderTapTrait';

const TEMPLATE_XFLOW_GATE = 'twcg-2112-twc-xflow-conf-pages-templates-gate';
const TEMPLATE_VIEW_TRIGGER_ID = 'twcg-2112-template-view-trigger';
const TEMPLATE_APPLIED_TRIGGER_ID = 'twcg-2112-template-applied-trigger';
const TEMPLATE_XFLOW_DELAY_MS = 3_000;
const TEMPLATE_XFLOW_SOURCE_COMPONENT = 'twcg2112ConfluenceTemplateXflow';
const TEMPLATE_XFLOW_SOURCE_CONTEXT = 'sideNavBanner';

const templateXflowMessages = defineMessages({
	meetingNotesHeading: {
		id: 'twc.sidenav.banner.template.xflow.meeting.notes.heading',
		defaultMessage: 'Automate meeting notes',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Meeting Notes template.',
	},
	projectPlanHeading: {
		id: 'twc.sidenav.banner.template.xflow.project.plan.heading',
		defaultMessage: 'Instantly create a project plan',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Project Plan template.',
	},
	eowStatusReportHeading: {
		id: 'twc.sidenav.banner.template.xflow.eow.status.report.heading',
		defaultMessage: 'Instantly create reports',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the End of Week Status Report template.',
	},
	decisionsHeading: {
		id: 'twc.sidenav.banner.template.xflow.decisions.heading',
		defaultMessage: 'Instantly capture decisions',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Decisions template.',
	},
	requirementsRaciHeading: {
		id: 'twc.sidenav.banner.template.xflow.requirements.raci.heading',
		defaultMessage: 'Instantly capture requirements',
		description:
			'Heading for the Loom cross-flow banner on a Confluence page created from the Requirements RACI template.',
	},
	genericHeading: {
		id: 'twc.sidenav.banner.template.xflow.generic.heading',
		defaultMessage: 'Don’t start from scratch',
		description:
			'Heading for the Loom cross-flow banner on a supported Confluence template without dedicated copy.',
	},
	tryItFree: {
		id: 'twc.sidenav.banner.template.xflow.try.it.free',
		defaultMessage: 'Try it free',
		description: 'CTA text for opening the Teamwork Collection cross-flow modal.',
	},
	notNow: {
		id: 'twc.sidenav.banner.template.xflow.not.now',
		defaultMessage: 'Not now',
		description: 'Action text for hiding the Teamwork Collection cross-flow banner for now.',
	},
});

const templateXflowHeadingByBlueprintModuleCompleteKey: Readonly<
	Record<string, MessageDescriptor>
> = {
	[CANONICAL_TEMPLATE_KEYS.MEETING_NOTES]: templateXflowMessages.meetingNotesHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_PLAN]: templateXflowMessages.projectPlanHeading,
	[CANONICAL_TEMPLATE_KEYS.EOW_STATUS_REPORT]: templateXflowMessages.eowStatusReportHeading,
	[CANONICAL_TEMPLATE_KEYS.DECISIONS]: templateXflowMessages.decisionsHeading,
	[CANONICAL_TEMPLATE_KEYS.REQUIREMENTS_RACI]: templateXflowMessages.requirementsRaciHeading,
	[CANONICAL_TEMPLATE_KEYS.RETROSPECTIVES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.BUSINESS_STATUS_UPDATE]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.ONE_ON_ONE_MEETING]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.RECURRING_MEETING_MINUTES]: templateXflowMessages.genericHeading,
	[CANONICAL_TEMPLATE_KEYS.PROJECT_STATUS]: templateXflowMessages.genericHeading,
};

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onboardingUrl: string;
	messageInstanceId: string;
	locale: string;
	redirectTo: 'jira' | 'confluence';
	experimentKey?: string;
	targetTenantId?: string;
	collectionEntitlementId?: string;
	waitForTrigger?: WaitForTriggerFunction;
};

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: noValidation(),
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({ message, request }) => {
			const { logger, orchestrationContext } = request;
			const { messageInstanceId, messageTemplateId, triggerId } = message;
			const { cloudId: tenantId, experimentKey, explicitEnableRequired } = message.context;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;
			const { locale } = orchestrationContext;

			// The template-xflow filter prevents legacy messages from reaching hydration.
			// Preserve a quiet no-op fallback in case a caller bypasses filters.
			if (!isTemplateXflowMessage) {
				return message.context;
			}

			const { targetTenantId, collectionEntitlementId, orgId } = message.context;

			if (!tenantId || typeof tenantId !== 'string') {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Tenant ID not available',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', { reason: 'tenant-id-not-available' })
					.incr();
				throw new Error('Tenant ID not available');
			}

			const env = request.executionRuntime.getEnvironmentType();
			const resolvedOrgId = typeof orgId === 'string' ? orgId : orchestrationContext.orgId;

			if (isTemplateXflowMessage && !resolvedOrgId) {
				logger.error(
					{ messageInstanceId, messageTemplateId, triggerId },
					'Org ID not available for template xflow message',
				);
				request.metrics
					.counter('twc-sidenav-banner.conf.error.count', {
						reason: 'template-xflow-org-id-not-available',
					})
					.incr();
				throw new Error('Org ID not available for template xflow message');
			}

			let templateXflowReminderTapTrait: Awaited<
				ReturnType<typeof getTemplateXflowReminderTapTrait>
			> = { tapReminderTrait: undefined, tapReminderTraitState: 'absent' };
			if (isTemplateXflowMessage) {
				const userId = orchestrationContext.recipient?.userId;
				const cachedTapClient = request.restApiClients?.cachedTapClient;

				if (userId && cachedTapClient) {
					try {
						templateXflowReminderTapTrait = await getTemplateXflowReminderTapTrait({
							userId,
							siteId: tenantId,
							tapProvider: new TapProvider(request.metrics, logger, cachedTapClient),
							logger,
						});
					} catch (error) {
						logger.error(
							{ error, messageInstanceId, messageTemplateId, triggerId },
							'Template xflow TAP traits unavailable',
						);
						request.metrics
							.counter('twc-sidenav-banner.conf.error.count', {
								reason: 'template-xflow-tap-traits-unavailable',
							})
							.incr();
					}
				}
			}

			const templateXflowModalUrl = resolvedOrgId
				? getHomeTwcXflowModalLink(
						env,
						resolvedOrgId,
						tenantId,
						TEMPLATE_XFLOW_SOURCE_COMPONENT,
						TEMPLATE_XFLOW_SOURCE_CONTEXT,
					)
				: undefined;

			const commonReturn = {
				...message.context,
				...(templateXflowReminderTapTrait.tapReminderTrait && {
					tapReminderTrait: templateXflowReminderTapTrait.tapReminderTrait,
				}),
				...(message.context.triggerType
					? { context: { triggerType: message.context.triggerType } }
					: {}),
				targetTenantId,
				collectionEntitlementId,
				messageInstanceId,
				locale,
			};

			const redirectTo = 'jira' as const;
			const onboardingUrl =
				templateXflowModalUrl ?? getLoomOnboardingUrl(env) ?? getAtlassianHomeUrl(env);

			request.metrics
				.counter('twc-sidenav-banner.conf.success.count', { bannerType: 'template-xflow' })
				.incr();

			return { ...commonReturn, redirectTo, onboardingUrl };
		},
	},
}));

const TemplateXflowBannerContent = ({
	heading,
	blueprintModuleCompleteKey,
	...props
}: MessageFrontendProps & {
	heading: string;
	blueprintModuleCompleteKey: string;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { messageDismissed, messageSnoozed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const additionalMetadataFromFrontend = {
		promptSurface: 'sideNavBanner',
		blueprintModuleCompleteKey,
	};
	const fireTemplateXflowReminderChanged = (
		reminderState: 'remind' | 'dismissed',
		sourceAction: 'remindMeLater' | 'tryItFree' | 'explicitDismiss',
	) => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'changed',
				actionSubject: 'twcTemplateXflowReminder',
				attributes: {
					blueprintModuleCompleteKey,
					experimentKey: TEMPLATE_XFLOW_EXPERIMENT_KEY,
					reminderState,
					source: 'sideNavBanner',
					sourceAction,
				},
			},
		}).fire();
	};

	const handleDismiss = () => {
		fireTemplateXflowReminderChanged('dismissed', 'explicitDismiss');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112Dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend,
		});
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleTryItFree = () => {
		fireTemplateXflowReminderChanged('dismissed', 'tryItFree');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112TryItFree',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'open' },
			additionalMetadataFromFrontend,
		});
		void messageDismissed();
		props.handleOnDismissed();
	};

	const handleNotNow = () => {
		fireTemplateXflowReminderChanged('remind', 'remindMeLater');
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'twcg2112RemindMeLater',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'snooze', snoozeDuration: 1 },
			additionalMetadataFromFrontend,
		});
		void messageSnoozed(1);
		props.handleOnDismissed();
	};

	return (
		<PersonalizedBanner
			heading={heading}
			locale={props.locale}
			recommendedApp="loom"
			onClose={handleDismiss}
			onCtaClick={handleTryItFree}
			ctaLabel={formatMessage(templateXflowMessages.tryItFree)}
			secondaryActionLabel={formatMessage(templateXflowMessages.notNow)}
			onSecondaryAction={handleNotNow}
			href={props.onboardingUrl}
			messageInstanceId={props.messageInstanceId}
			experimentKey={props.experimentKey}
			actionTier={props.actionTier}
			targetTenantId={props.targetTenantId}
			collectionEntitlementId={props.collectionEntitlementId}
			surfaceProduct="confluence"
		/>
	);
};

const TemplateXflowBanner = (props: MessageFrontendProps) => {
	const { tapReminderTrait, waitForTrigger } = props;
	const { formatMessage } = useIntl();
	const { isActive: isTemplateViewTriggerActive } = useTrigger(TEMPLATE_VIEW_TRIGGER_ID);
	const { isActive: isTemplateAppliedTriggerActive } = useTrigger(TEMPLATE_APPLIED_TRIGGER_ID);
	const { fireTrigger: fireTemplateViewTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_VIEW_TRIGGER_ID,
	});
	const { fireTrigger: fireTemplateAppliedTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_APPLIED_TRIGGER_ID,
	});
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const currentPageTemplateData = templateXflow?.currentPageTemplateData;
	const blueprintModuleCompleteKey = currentPageTemplateData?.blueprintModuleCompleteKey;
	const templateHeadingDescriptor = blueprintModuleCompleteKey
		? templateXflowHeadingByBlueprintModuleCompleteKey[blueprintModuleCompleteKey]
		: undefined;
	const templateHeading = templateHeadingDescriptor
		? formatMessage(templateHeadingDescriptor)
		: undefined;
	const isTemplateXflowCohortEligible =
		currentPageTemplateData?.isEligible === true || tapReminderTrait === 'remind';
	const isTemplateXflowDismissed =
		tapReminderTrait === 'dismissed' ||
		templateXflow?.reminderState === 'dismissed' ||
		templateXflow?.isTerminallyDismissed === true;
	const isTemplateTriggerActive =
		Boolean(isTemplateViewTriggerActive) || Boolean(isTemplateAppliedTriggerActive);
	const isTemplateXflowGateEnabled = fg(TEMPLATE_XFLOW_GATE);
	const shouldStartTemplateXflowDelay =
		isTemplateXflowGateEnabled &&
		!isTemplateXflowDismissed &&
		isTemplateXflowCohortEligible &&
		isTemplateTriggerActive &&
		Boolean(currentPageTemplateData?.contentId) &&
		Boolean(templateHeadingDescriptor);
	const [hasTemplateXflowDelayElapsed, setHasTemplateXflowDelayElapsed] = useState(false);

	useEffect(() => {
		setHasTemplateXflowDelayElapsed(false);

		if (!shouldStartTemplateXflowDelay) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setHasTemplateXflowDelayElapsed(true);
		}, TEMPLATE_XFLOW_DELAY_MS);

		return () => clearTimeout(timeoutId);
	}, [
		blueprintModuleCompleteKey,
		currentPageTemplateData?.contentId,
		shouldStartTemplateXflowDelay,
	]);

	const resetTemplateTriggers = useCallback(() => {
		fireTemplateViewTrigger({ isActive: false });
		fireTemplateAppliedTrigger({ isActive: false });
	}, [fireTemplateAppliedTrigger, fireTemplateViewTrigger]);

	if (
		isTemplateXflowDismissed ||
		!templateHeading ||
		!blueprintModuleCompleteKey ||
		!waitForTrigger
	) {
		return null;
	}

	return waitForTrigger({
		isTriggerActive: hasTemplateXflowDelayElapsed,
		messageContent: (
			<TemplateXflowBannerContent
				{...props}
				heading={templateHeading}
				blueprintModuleCompleteKey={blueprintModuleCompleteKey}
			/>
		),
		onTeardown: resetTemplateTriggers,
	});
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: (props: MessageFrontendProps) => {
			const { experimentKey, explicitEnableRequired } = props;
			const isTemplateXflowMessage =
				experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY && explicitEnableRequired === true;

			if (isTemplateXflowMessage) {
				return <TemplateXflowBanner {...props} />;
			}

			return null;
		},
	},
});
